import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useBot } from "@/context/BotContext";
import Layout from "@/components/Layout";
import {
  TrendingUp, TrendingDown, ChevronLeft, ChevronRight,
} from "lucide-react";

const API = "/api";

const TradeHistory = () => {
  const { token } = useAuth();
  const { selectedBotId, mode } = useBot();
  const [trades, setTrades] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchTrades = useCallback(async () => {
    try {
      const params = selectedBotId ? { bot_id: selectedBotId, limit, skip: (page - 1) * limit } : { mode, limit, skip: (page - 1) * limit };
      const r = await axios.get(`${API}/trades`, { params, headers });
      setTrades(r.data.trades || []);
      setTotal(r.data.total || 0);
    } catch {}
  }, [selectedBotId, mode, page]);

  useEffect(() => { fetchTrades(); }, [fetchTrades]);

  const totalPages = Math.ceil(total / limit);

  return (
    <Layout>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="text-[21px] font-bold tracking-tight" style={{ color: 'var(--text-primary)' }}>Trade History</h1>
          <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{total} trades</span>
        </div>

        <div className="card-base overflow-hidden">
          {/* Header */}
          <div className="table-header hidden md:grid grid-cols-8 gap-2 px-4 py-3">
            {["Time", "Direction", "Outcome", "BTC Move", "Entry %", "Bet", "P/L", "Ladder"].map(h => (
              <div key={h}>{h}</div>
            ))}
          </div>

          {/* Rows */}
          {trades.length === 0 ? (
            <div className="p-12 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>No trades yet</div>
          ) : trades.map((t, i) => (
            <div key={t.id || i} data-testid={`history-row-${i}`}
              className="grid grid-cols-4 md:grid-cols-8 gap-2 px-4 py-3 table-row text-[13px]">
              <div style={{ color: 'var(--text-secondary)' }}>
                {t.timestamp ? new Date(t.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : "--"}
              </div>
              <div className="flex items-center gap-1.5">
                {t.direction === "UP" ? <TrendingUp className="w-3.5 h-3.5" style={{ color: 'var(--accent-teal)' }} />
                  : <TrendingDown className="w-3.5 h-3.5" style={{ color: 'var(--accent-red)' }} />}
                <span style={{ color: t.direction === "UP" ? 'var(--accent-teal)' : 'var(--accent-red)' }}>{t.direction}</span>
              </div>
              <div>
                <span className={t.outcome === "win" ? "badge-success" : "badge-error"}>
                  {t.outcome?.toUpperCase()}
                </span>
              </div>
              <div style={{ color: t.actual_outcome === "UP" ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                BTC {t.actual_outcome}
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>{t.entry_percent ? `${t.entry_percent}%` : "--"}</div>
              <div style={{ color: 'var(--text-primary)' }}>${(t.bet_amount || 0).toFixed(2)}</div>
              <div className="font-bold" style={{ color: (t.profit_loss || 0) >= 0 ? 'var(--accent-teal)' : 'var(--accent-red)' }}>
                {(t.profit_loss || 0) >= 0 ? "+" : ""}${(t.profit_loss || 0).toFixed(2)}
              </div>
              <div>
                <span className={
                  t.ladder_action === "reset" ? "badge-success"
                  : t.ladder_action === "failed" ? "badge-error"
                  : "badge-warning"
                }>{t.ladder_action} S{t.ladder_step}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="btn-ghost p-2 rounded-lg disabled:opacity-30">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {page} / {totalPages}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="btn-ghost p-2 rounded-lg disabled:opacity-30">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TradeHistory;
